/**
 * DEC-001: View Registry and Resolution
 */

import type { DocTypeView, OrderContext, ResolvedView, ViewStyleKey } from './types';

class ViewRegistry {
  private views: Map<string, DocTypeView[]> = new Map();

  registerViews(doctype: string, views: DocTypeView[]) {
    this.views.set(doctype, views);
  }

  getViews(doctype: string): DocTypeView[] {
    return this.views.get(doctype) || [];
  }

  /**
   * Get active view for DocType/context combination.
   * 
   * In dev mode: returns 'active' or 'draft' views (prefers active).
   * In prod: only returns 'active' views.
   * 
   * If preferredStyle is provided, filters views by style first, then applies
   * context/status priority. If no views match the preferred style, falls back
   * to all available views.
   */
  getActiveView(
    doctype: string,
    context?: OrderContext,
    devMode?: boolean,
    preferredStyle?: ViewStyleKey
  ): DocTypeView | undefined {
    const views = this.getViews(doctype);
    
    const statusFilter = devMode ? ['active', 'draft'] : ['active'];
    let availableViews = views.filter(v => statusFilter.includes(v.status));
    
    // Filter by preferred style if specified
    if (preferredStyle) {
      const styleFiltered = availableViews.filter(v => v.style === preferredStyle);
      if (styleFiltered.length > 0) {
        availableViews = styleFiltered;
      }
      // If no views match style, keep original availableViews for fallback
    }
    
    if (context) {
      // Prefer active, but in dev mode allow draft if no active exists
      const activeSpecific = availableViews.find(
        v => v.context === context && v.status === 'active'
      );
      if (activeSpecific) return activeSpecific;
      
      // Dev-only: allow draft if no active found
      if (devMode) {
        const draftSpecific = availableViews.find(
          v => v.context === context && v.status === 'draft'
        );
        if (draftSpecific) return draftSpecific;
      }
    }
    
    // Fall back to generic (no context) - prefer active
    const genericActive = availableViews.find(
      v => !v.context && v.status === 'active'
    );
    if (genericActive) return genericActive;

    // Dev-only: allow generic draft if no active
    if (devMode) {
      const genericDraft = availableViews.find(v => !v.context && v.status === 'draft');
      if (genericDraft) return genericDraft;
    }

    // Last resort: if no generic view exists, use any available active view
    // (e.g., Sales Order only has an 'ecommerce' context view — still show it)
    const anyActive = availableViews.find(v => v.status === 'active');
    if (anyActive) return anyActive;

    return undefined;
  }
}

// Singleton instance
export const viewRegistry = new ViewRegistry();

/**
 * Resolve which view to use (System vs Experience)
 * 
 * This is the canonical entry point for view resolution.
 * 
 * @param preferredStyle - Preferred ViewStyleKey or 'auto' for default behavior
 */
export function resolveView(
  doctype: string,
  context?: OrderContext,
  systemMode?: boolean,
  devMode?: boolean,
  preferredStyle?: ViewStyleKey | 'auto'
): ResolvedView {
  // Force system mode if requested
  if (systemMode) {
    return {
      mode: 'system',
      style: 'erpnext',
    };
  }

  // Convert 'auto' to undefined (use default resolution)
  const styleToUse = preferredStyle === 'auto' ? undefined : preferredStyle;
  
  // Try to get experience view with style preference
  const experienceView = viewRegistry.getActiveView(
    doctype, 
    context, 
    devMode,
    styleToUse
  );
  
  if (experienceView) {
    return {
      mode: 'experience',
      view: experienceView,
      style: experienceView.style,
    };
  }
  
  // Warn in dev mode if style preference couldn't be satisfied
  if (devMode && styleToUse) {
    if (import.meta.env?.DEV) {
      console.warn(
        '[resolveView] No view found for preferred style',
        { doctype, context, preferredStyle: styleToUse }
      );
    }
  }
  
  // Fallback: Try without style preference
  if (styleToUse) {
    const fallbackView = viewRegistry.getActiveView(doctype, context, devMode);
    if (fallbackView) {
      return {
        mode: 'experience',
        view: fallbackView,
        style: fallbackView.style,
      };
    }
  }

  // Final fallback to system view
  return {
    mode: 'system',
    style: 'erpnext',
  };
}

